import useStatsigExperiment from 'modules/common/hooks/useStatsigExperiment';
import { EXP_HUB_2109_MESSAGE_CENTER_COPY } from 'modules/common/constants/experiments';

interface MessageCenterCopy {
    /** Top bar chip that opens the Help Agent. */
    helpButtonLabel: string;
    /** Top bar chip that opens the Message Center drawer (desktop only). */
    messagesButtonLabel: string;
    /** Drawer heading, and the drawer's accessible name. */
    drawerTitle: string;
    /** Plus-menu option that opens the Help Agent. */
    helpAgentOptionLabel: string;
    /** Plus-menu option that starts a written conversation with the team. */
    newConversationOptionLabel: string;
    /** Whether the top bar leads with the Help Agent chip instead of the Message Center chip. */
    isHelpFirst: boolean;
}

/** The `variation` parameter value on the treatment group in the Statsig console. */
const TREATMENT = 'treatment';

const CONTROL_COPY: MessageCenterCopy = {
    helpButtonLabel: 'Get Help',
    messagesButtonLabel: 'Message Center',
    drawerTitle: 'Message Center',
    helpAgentOptionLabel: 'Get an instant answer',
    newConversationOptionLabel: 'Ask our team',
    isHelpFirst: false,
};

const VARIATION_COPY: MessageCenterCopy = {
    helpButtonLabel: 'Get help',
    messagesButtonLabel: 'Messages',
    drawerTitle: 'Messages',
    helpAgentOptionLabel: 'Get help now',
    newConversationOptionLabel: 'Leave us a note',
    isHelpFirst: true,
};

/**
 * HUB-2109: copy test on the help entry points, run on 50% of members.
 *
 * Every string the experiment touches lives here so the top bar and the drawer can never
 * disagree about what the surface is called. Reading it through `useStatsigExperiment` is
 * what fires the `Experiment Viewed` exposure the Amplitude readout joins on, and that
 * exposure is deduped per user, so it costs nothing to call this from each consumer.
 *
 * The new copy ships only when Statsig positively returns the treatment value. Matching on
 * `variation` rather than the hook's `isVariation` matters: `isVariation` is `!isControl`, so
 * an experiment the client can't resolve at all (unknown name, client stubbed out) reads as
 * the treatment arm. Control is today's copy and the only safe fallback.
 */
export default function useMessageCenterCopy(): MessageCenterCopy {
    const { variation } = useStatsigExperiment(EXP_HUB_2109_MESSAGE_CENTER_COPY);

    return variation === TREATMENT ? VARIATION_COPY : CONTROL_COPY;
}
